import { DOCUMENT } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  effect,
  Inject,
  input,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { watchBodyDarkMode$ } from '@examdojo/styles';
import * as go from 'gojs';
import { type GojsDiagramColorTheme, type GojsDiagramModel } from '../model';
import { type GojsResolvedColors, resolveGojsColorPalette, resolveGojsStructuralColors } from './gojs-color.util';
import { createGojsLayout, hexToRgba, parseSimpleLatexSegments } from './gojs-diagram.util';
import { resolveGojsIcon } from './gojs-icon.util';

@Component({
  selector: 'dojo-gojs-diagram-viewer',
  standalone: true,
  templateUrl: './gojs-diagram-viewer.component.html',
  styleUrls: ['./gojs-diagram-viewer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GojsDiagramViewerComponent implements AfterViewInit, OnDestroy {
  constructor(@Inject(DOCUMENT) private readonly document: Document) {
    effect(() => {
      const model = this.model();
      const _darkMode = this.isDark();
      if (this.diagram && model) {
        this.updateModel(model);
      }
    });
  }

  private readonly isDark = toSignal(watchBodyDarkMode$(this.document), { initialValue: false });

  readonly model = input.required<GojsDiagramModel>();
  readonly layoutType = input<string>('tree');
  readonly height = input<number | undefined>(undefined);
  readonly interactive = input<boolean>(false);
  readonly hoverHighlight = input<boolean>(false);

  @ViewChild('diagramDiv', { static: false }) diagramDiv!: ElementRef<HTMLDivElement>;

  private diagram: go.Diagram | null = null;
  private rafId: number | null = null;
  /** Map from hover_group ID to { targets, color } for the group's trigger config. */
  private readonly hoverGroupConfig = new Map<string, { targets: string[]; color: string }>();

  ngAfterViewInit(): void {
    // Defer initialization to the next animation frame so the container div
    // has its final dimensions. Without this, GoJS may compute layouts with
    // zero width, causing all nodes to pile up at the origin.
    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;
      this.initDiagram();
    });
  }

  ngOnDestroy(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    if (this.diagram) {
      this.diagram.div = null;
      this.diagram = null;
    }
  }

  private initDiagram(): void {
    const div = this.diagramDiv?.nativeElement;
    if (!div) {
      return;
    }

    const $ = go.GraphObject.make;
    const isInteractive = this.interactive();
    const hoverHL = this.hoverHighlight();
    const structural = resolveGojsStructuralColors(this.document);

    this.diagram = $(go.Diagram, div, {
      layout: this.createLayout(),
      isReadOnly: !isInteractive,
      allowSelect: isInteractive,
      allowMove: isInteractive,
      allowDelete: isInteractive,
      allowLink: isInteractive,
      allowRelink: isInteractive,
      autoScale: go.AutoScale.Uniform,
      'animationManager.isEnabled': false,
      'undoManager.isEnabled': isInteractive,
      contentAlignment: go.Spot.Center,
      padding: new go.Margin(10),
    });

    // Hover handlers for label nodes (only active when hoverHighlight is on)
    // Also handles hover_group interactions for Venn-diagram-style color cascading.
    const nodeHover = {
      mouseEnter: (_e: go.InputEvent, obj: go.GraphObject) => {
        if (!(obj instanceof go.Node)) {
          return;
        }
        if (obj.data?.hover_group) {
          this.setHoverGroupHighlight(obj, true);
        } else if (hoverHL && obj.data?.is_label) {
          this.setLabelHighlight(obj, true);
        }
      },
      mouseLeave: (_e: go.InputEvent, obj: go.GraphObject) => {
        if (!(obj instanceof go.Node)) {
          return;
        }
        if (obj.data?.hover_group) {
          this.setHoverGroupHighlight(obj, false);
        } else if (hoverHL && obj.data?.is_label) {
          this.setLabelHighlight(obj, false);
        }
      },
    };

    // Hover handlers for links
    const linkHover = hoverHL
      ? {
          mouseEnter: (_e: go.InputEvent, obj: go.GraphObject) => {
            if (obj instanceof go.Link) {
              this.setLinkHighlight(obj, true);
            }
          },
          mouseLeave: (_e: go.InputEvent, obj: go.GraphObject) => {
            if (obj instanceof go.Link) {
              this.setLinkHighlight(obj, false);
            }
          },
        }
      : {};

    // Node template
    this.diagram.nodeTemplate = $(
      go.Node,
      'Auto',
      {
        locationSpot: go.Spot.Center,
        cursor: 'default',
        ...nodeHover,
      },
      new go.Binding('location', 'loc', go.Point.parse),
      new go.Binding('cursor', '', (data: Record<string, unknown>) => {
        if (data['hover_group']) {
          return 'pointer';
        }
        if (data['is_label'] && hoverHL) {
          return 'pointer';
        }
        return isInteractive ? 'pointer' : 'default';
      }),
      $(
        go.Shape,
        {
          figure: 'RoundedRectangle',
          strokeWidth: 0,
          stroke: 'transparent',
          parameter1: 8,
        },
        new go.Binding('figure', 'shape'),
        new go.Binding('fill', 'bg_color'),
        new go.Binding('stroke', 'border_color'),
        new go.Binding('strokeWidth', 'has_border', (b: boolean | number) =>
          b ? (typeof b === 'number' ? b : 1.5) : 0,
        ),
        // Fixed-size shapes (for Venn diagram circles etc.)
        new go.Binding('desiredSize', '', (data: Record<string, unknown>) => {
          if (data['node_width'] && data['node_height']) {
            return new go.Size(data['node_width'] as number, data['node_height'] as number);
          }
          return new go.Size(NaN, NaN);
        }),
      ),
      $(
        go.Panel,
        'Horizontal',
        { margin: new go.Margin(8, 14) },
        // Hide content panel for shape-only nodes (no text, no icon, no latex)
        new go.Binding('visible', '', (data: Record<string, unknown>) => {
          return !!(data['text'] || data['icon_text'] || data['icon_src']);
        }),
        // Emoji icon (rendered as text)
        $(
          go.TextBlock,
          {
            font: '14px Inter, system-ui, sans-serif',
            margin: new go.Margin(0, 4, 0, 0),
            visible: false,
          },
          new go.Binding('text', 'icon_text'),
          new go.Binding('visible', 'icon_text', (t: string) => !!t),
          new go.Binding('font', '', (data: Record<string, unknown>) => {
            const size = (data['font_size'] as number) ?? 14;
            return `500 ${size}px Inter, system-ui, sans-serif`;
          }),
        ),
        // FA / image icon (rendered as picture — FA pre-rendered to PNG via canvas)
        $(
          go.Picture,
          {
            width: 18,
            height: 18,
            margin: new go.Margin(0, 6, 0, 0),
            visible: false,
            imageStretch: go.ImageStretch.Uniform,
          },
          new go.Binding('source', 'icon_src'),
          new go.Binding('visible', 'icon_src', (s: string) => !!s),
        ),
        $(
          go.TextBlock,
          {
            font: '14px Inter, system-ui, sans-serif',
            stroke: structural.textDefault,
          },
          new go.Binding('text', 'text'),
          new go.Binding('stroke', 'text_color'),
          new go.Binding('font', '', (data: Record<string, unknown>) => {
            const size = (data['font_size'] as number) ?? 14;
            if (data['is_math']) {
              return `italic ${size}px "Times New Roman", Georgia, serif`;
            }
            const weight = data['font_weight'] === 'bold' ? 'bold' : '500';
            return `${weight} ${size}px Inter, system-ui, sans-serif`;
          }),
        ),
      ),
    );

    // Math/label node template — uses Spot panel so text is never clipped by the shape
    this.diagram.nodeTemplateMap.add(
      'math',
      $(
        go.Node,
        'Spot',
        {
          locationSpot: go.Spot.Center,
          cursor: 'default',
          ...nodeHover,
        },
        new go.Binding('location', 'loc', go.Point.parse),
        new go.Binding('cursor', 'is_label', (v: boolean) =>
          v && hoverHL ? 'pointer' : isInteractive ? 'pointer' : 'default',
        ),
        $(
          go.Shape,
          {
            figure: 'RoundedRectangle',
            strokeWidth: 0,
            stroke: 'transparent',
            parameter1: 8,
          },
          new go.Binding('figure', 'shape'),
          new go.Binding('fill', 'bg_color'),
          new go.Binding('stroke', 'border_color'),
          new go.Binding('strokeWidth', 'has_border', (b: boolean | number) =>
            b ? (typeof b === 'number' ? b : 1.5) : 0,
          ),
          new go.Binding('desiredSize', '', (data: Record<string, unknown>) => {
            if (data['node_width'] && data['node_height']) {
              return new go.Size(data['node_width'] as number, data['node_height'] as number);
            }
            return new go.Size(NaN, NaN);
          }),
        ),
        $(
          go.TextBlock,
          {
            alignment: go.Spot.Center,
            font: 'italic 14px serif',
            stroke: structural.textDefault,
          },
          new go.Binding('text', 'text'),
          new go.Binding('stroke', 'text_color'),
          new go.Binding('font', '', (data: Record<string, unknown>) => {
            const size = (data['font_size'] as number) ?? 14;
            return `italic ${size}px serif`;
          }),
        ),
      ),
    );

    // Link template
    this.diagram.linkTemplate = $(
      go.Link,
      {
        routing: go.Routing.Orthogonal,
        corner: 8,
        selectable: isInteractive,
        cursor: hoverHL || isInteractive ? 'pointer' : 'default',
        ...linkHover,
      },
      new go.Binding('routing', 'routing', (r: string) => {
        switch (r) {
          case 'Orthogonal':
            return go.Routing.Orthogonal;
          case 'AvoidsNodes':
            return go.Routing.AvoidsNodes;
          default:
            return go.Routing.Normal;
        }
      }),
      new go.Binding('curve', 'curve', (c: string) => (c === 'Bezier' ? go.Curve.Bezier : go.Curve.None)),
      new go.Binding('curviness', 'curviness'),
      $(
        go.Shape,
        { strokeWidth: 1.5, stroke: structural.linkColor },
        new go.Binding('stroke', 'color'),
        new go.Binding('strokeWidth', 'width'),
      ),
      $(
        go.Shape,
        { toArrow: 'Triangle', strokeWidth: 0, fill: structural.linkColor, scale: 0.8 },
        new go.Binding('fill', 'color'),
        new go.Binding('toArrow', 'to_arrow'),
        new go.Binding('visible', 'to_arrow', (a: string) => a !== 'None'),
      ),
    );

    this.updateModel(this.model());
  }

  private createLayout(): go.Layout {
    return createGojsLayout(this.layoutType());
  }

  private resolveColors(
    theme: GojsDiagramColorTheme | undefined,
    palette: Record<string, GojsResolvedColors>,
  ): GojsResolvedColors {
    return palette[theme ?? 'neutral'];
  }

  private updateModel(model: GojsDiagramModel): void {
    if (!this.diagram) {
      return;
    }

    const hoverHL = this.hoverHighlight();
    const palette = resolveGojsColorPalette(this.document);
    const structural = resolveGojsStructuralColors(this.document);
    const neutral = palette['neutral'];

    const nodeDataArray = model.nodes.map((n) => {
      const colors = this.resolveColors(n.color_theme, palette);
      let iconData: { icon_text: string; icon_src: string } = { icon_text: '', icon_src: '' };
      try {
        iconData = resolveGojsIcon(n.icon, structural.textDefault);
      } catch {
        // Icon resolution can fail for unknown FA icons — fall back to no icon
      }

      // Direct fill with opacity → RGBA
      let bgColor = colors.bg;
      if (n.fill) {
        bgColor = n.fill_opacity !== undefined && n.fill_opacity < 1 ? hexToRgba(n.fill, n.fill_opacity) : n.fill;
      }

      // Direct stroke overrides
      let borderColor = colors.border;
      let hasBorder: boolean | number = n.border ?? false;
      if (n.stroke) {
        borderColor = n.stroke;
        hasBorder = n.stroke_width ?? 2;
      }

      // Parse LaTeX into native GoJS text with math-like font
      let displayText = n.text ?? '';
      let textColor = colors.text;
      let isMath = false;
      if (n.latex) {
        const parsed = parseSimpleLatexSegments(n.latex);
        displayText = parsed.text;
        if (parsed.color) {
          textColor = parsed.color;
        }
        // Use italic serif math template unless font_weight is set (then use Auto/Inter template)
        isMath = !n.font_weight;
      }

      // hover_only nodes are invisible by default (appear only when their group is targeted)
      if (n.hover_only) {
        textColor = 'transparent';
      }

      // Detect label nodes (text + border) for hover highlight
      const isLabel = !!(n.text && n.border);
      const origBgColor = bgColor;
      const origTextColor = textColor;
      const origBorderColor = borderColor;

      if (hoverHL && isLabel) {
        bgColor = neutral.bg;
        textColor = neutral.text;
        borderColor = neutral.border;
      }

      return {
        key: n.key,
        text: displayText,
        ...(isMath && { category: 'math' }),
        color_theme: n.color_theme ?? 'neutral',
        bg_color: bgColor,
        text_color: textColor,
        border_color: borderColor,
        has_border: hasBorder,
        icon: n.icon ?? '',
        icon_text: iconData.icon_text,
        icon_src: iconData.icon_src,
        shape: n.shape ?? 'RoundedRectangle',
        font_size: n.font_size,
        font_weight: n.font_weight,
        node_width: n.width,
        node_height: n.height,
        is_math: isMath,
        is_label: isLabel,
        orig_bg_color: origBgColor,
        orig_text_color: origTextColor,
        orig_border_color: origBorderColor,
        hover_group: n.hover_group,
        hover_targets: n.hover_targets,
        hover_color: n.hover_color,
        hover_only: n.hover_only ?? false,
        ...(n.loc && { loc: n.loc }),
      };
    });

    // Build hover group config map from nodes that define targets
    this.hoverGroupConfig.clear();
    for (const n of model.nodes) {
      if (n.hover_group && n.hover_targets && n.hover_color) {
        this.hoverGroupConfig.set(n.hover_group, {
          targets: n.hover_targets,
          color: n.hover_color,
        });
      }
    }

    const linkDataArray = model.links.map((l) => {
      const origColor = l.color ?? structural.linkColor;
      return {
        from: l.from,
        to: l.to,
        color: hoverHL ? structural.mutedLinkColor : origColor,
        orig_color: origColor,
        width: l.width ?? 1.5,
        to_arrow: l.to_arrow ?? 'Triangle',
        routing: l.routing ?? 'Orthogonal',
        curve: l.curve ?? 'None',
        curviness: l.curviness ?? NaN,
      };
    });

    this.diagram.model = new go.GraphLinksModel(nodeDataArray, linkDataArray);
  }

  /** Highlight or dim a label node and all its connected links. */
  private setLabelHighlight(node: go.Node, highlight: boolean): void {
    const diagram = node.diagram;
    if (!diagram) {
      return;
    }
    const model = diagram.model;
    const data = node.data;
    const palette = resolveGojsColorPalette(this.document);
    const structural = resolveGojsStructuralColors(this.document);
    const neutral = palette['neutral'];

    diagram.startTransaction('hover');
    model.setDataProperty(data, 'bg_color', highlight ? data.orig_bg_color : neutral.bg);
    model.setDataProperty(data, 'text_color', highlight ? data.orig_text_color : neutral.text);
    model.setDataProperty(data, 'border_color', highlight ? data.orig_border_color : neutral.border);
    node.linksConnected.each((link) => {
      model.setDataProperty(link.data, 'color', highlight ? link.data.orig_color : structural.mutedLinkColor);
    });
    diagram.commitTransaction('hover');
  }

  /** Highlight or dim a link and its source label node. */
  private setLinkHighlight(link: go.Link, highlight: boolean): void {
    const diagram = link.diagram;
    if (!diagram) {
      return;
    }
    const model = diagram.model;
    const palette = resolveGojsColorPalette(this.document);
    const structural = resolveGojsStructuralColors(this.document);
    const neutral = palette['neutral'];

    diagram.startTransaction('hover');
    model.setDataProperty(link.data, 'color', highlight ? link.data.orig_color : structural.mutedLinkColor);
    const fromNode = link.fromNode;
    if (fromNode?.data?.is_label) {
      model.setDataProperty(fromNode.data, 'bg_color', highlight ? fromNode.data.orig_bg_color : neutral.bg);
      model.setDataProperty(fromNode.data, 'text_color', highlight ? fromNode.data.orig_text_color : neutral.text);
      model.setDataProperty(
        fromNode.data,
        'border_color',
        highlight ? fromNode.data.orig_border_color : neutral.border,
      );
    }
    diagram.commitTransaction('hover');
  }

  /** Hover group highlight: colors targeted groups with the accent color, greys out the rest. */
  private setHoverGroupHighlight(node: go.Node, highlight: boolean): void {
    const diagram = node.diagram;
    if (!diagram) {
      return;
    }

    const group: string = node.data?.hover_group;
    if (!group) {
      return;
    }

    const config = this.hoverGroupConfig.get(group);
    if (!config) {
      return;
    }

    const model = diagram.model;
    const structural = resolveGojsStructuralColors(this.document);
    const accentColor = config.color;
    const accentFill = hexToRgba(accentColor, 0.1);
    const targetSet = new Set(config.targets);
    const greyFill = hexToRgba(structural.greyFillBase, 0.06);

    diagram.startTransaction('hoverGroup');
    diagram.nodes.each((n) => {
      const nGroup: string = n.data?.hover_group;
      if (!nGroup) {
        return;
      }

      const isTarget = targetSet.has(nGroup);
      const hasSize = n.data.node_width && n.data.node_height;

      if (highlight) {
        if (isTarget) {
          if (hasSize) {
            model.setDataProperty(n.data, 'bg_color', accentFill);
            model.setDataProperty(n.data, 'border_color', accentColor);
          } else {
            model.setDataProperty(n.data, 'text_color', accentColor);
          }
        } else {
          if (hasSize) {
            model.setDataProperty(n.data, 'bg_color', greyFill);
            model.setDataProperty(n.data, 'border_color', structural.greyStroke);
          } else {
            model.setDataProperty(n.data, 'text_color', n.data.hover_only ? 'transparent' : structural.greyText);
          }
        }
      } else {
        model.setDataProperty(n.data, 'bg_color', n.data.orig_bg_color);
        model.setDataProperty(n.data, 'text_color', n.data.orig_text_color);
        model.setDataProperty(n.data, 'border_color', n.data.orig_border_color);
      }
    });
    diagram.commitTransaction('hoverGroup');
  }
}
